/**
 * Interests aggregated from the evidence attached to a subject.
 *
 * **Every number here is derived at read time, and the page says so.** There is no
 * interest table and there must not be one: an inference written down becomes
 * indistinguishable from something a source actually said, which is what ADR-0038 refuses.
 *
 * **The confidence figure is the one to get right.** `strongest_extraction` is the highest
 * extraction confidence among the supporting assertions — how certain a *parser* was that
 * the text says this, not how likely the person is to hold the interest. It is rendered as
 * a percentage because that reads better, and labelled as extraction confidence every
 * single time it appears, because "86%" beside a person's name invites exactly the reading
 * the whole design refuses.
 *
 * **Sources and independent sources are shown separately**, and that is not redundancy.
 * Two plugins reading the same page are two sources and one piece of information;
 * corroboration is the only thing entitled to say which is which, and collapsing them into
 * one number would overstate the support.
 */

import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { request } from "@/lib/api";
import { Badge, EmptyState, ErrorState, Loading } from "@/components/ui";

interface InterestEvidence {
  assertion_id: string;
  link_id: string;
  source: string;
  observed_at: string;
  extraction_confidence: number;
  evidence_type: string;
  evidence_url: string;
  information_credibility: number;
}

interface Interest {
  interest: string;
  label: string;
  evidence_count: number;
  source_count: number;
  independent_source_count: number;
  information_credibility: number;
  strongest_extraction: number;
  first_observed: string;
  last_observed: string;
  evidence: InterestEvidence[];
}

/**
 * The interests panel.
 *
 * @param props - The subject to read.
 * @returns The panel.
 */
export function SubjectInterestsPanel({ subjectId }: { subjectId: string }): ReactElement {
  const interests = useQuery({
    queryKey: ["subject-interests", subjectId],
    queryFn: () => request<Interest[]>(`/research/subjects/${subjectId}/interests`),
  });

  if (interests.isLoading) return <Loading label="Loading interests" />;
  if (interests.error != null) return <ErrorState error={interests.error} />;

  const rows = interests.data ?? [];

  return (
    <section
      className="space-y-3"
      aria-label="Interests panel"
      data-testid="subject-panel-interests"
    >
      <p className="max-w-2xl text-xs text-muted">
        Grouped from the <code>has_interest</code> claims on the attached evidence, and
        computed each time this is opened — nothing here is stored. An interest is what
        sources mentioned near this person, not a conclusion about them.
      </p>

      {rows.length === 0 ? (
        <EmptyState>
          No interests yet. They appear once evidence mentioning one is attached.
        </EmptyState>
      ) : (
        <ul className="space-y-2" data-testid="interest-list">
          {rows.map((row) => (
            <li
              key={row.interest}
              className="space-y-2 rounded border border-line p-3"
              data-testid="interest-row"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{row.label}</span>
                <Badge tone="muted">
                  {row.evidence_count} piece(s) of evidence
                </Badge>
                <Badge tone={row.independent_source_count > 0 ? "ok" : "muted"}>
                  {row.independent_source_count} independent of {row.source_count} source(s)
                </Badge>
                <Badge tone="muted">credibility {row.information_credibility}/6</Badge>
              </div>

              <p className="text-xs text-muted" data-testid="interest-confidence">
                {/* The label travels with the number, every time. Rendered apart from the
                    figure it would be one copy-edit away from reading as a probability
                    about the person. */}
                Strongest extraction confidence{" "}
                {Math.round(row.strongest_extraction * 100)}% — how certain a parser was
                that the text says this, not how likely the person is to hold the interest.
              </p>

              <p className="text-xs text-muted">
                Seen {row.first_observed.slice(0, 10)} to {row.last_observed.slice(0, 10)}
              </p>

              <details className="text-xs">
                <summary className="cursor-pointer text-muted">
                  What supports it ({row.evidence.length})
                </summary>
                <ul className="mt-1 space-y-1">
                  {row.evidence.map((item) => (
                    <li key={item.assertion_id} className="text-muted">
                      <span className="font-mono">{item.source}</span>{" "}
                      {item.evidence_type.replaceAll("_", " ")}{" "}
                      {item.evidence_url !== "" && (
                        <a
                          className="text-accent"
                          href={item.evidence_url}
                          rel="noreferrer noopener"
                          target="_blank"
                        >
                          source
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
